/**
 * Generative melody: every theme gets a deterministic little tune.
 *
 * The theme's audio params (key, scale, bpm, lead) seed an Rng, which picks a
 * 2-bar *motif* (rhythm + melodic contour) and an 8-bar chord progression.
 * The motif is developed into an 8-bar phrase:
 *
 *   bars 1–2  A    the motif — identical in every phrase, so the level is recognisable
 *   bars 3–4  A′   the motif ending on the dominant ("question")
 *   bars 5–6  B    the motif sequenced up/down a step or two
 *   bars 7–8  A″   the motif ending on the tonic ("answer")
 *
 * Phrase 0 is the canonical tune; later phrases add gentle, deterministic
 * variation (neighbour-note nudges, split notes) outside bars 1–2, and every
 * 4th phrase rests in the B section so the loop never nags. Strong-beat notes
 * are snapped to the bar's chord tones, so the melody always sits on the pad.
 *
 * Everything is in scale degrees (see pitch.ts). Pure and DOM-free.
 */
import { Rng, deriveSeed, type ThemeAudio } from '@dropstack/core';
import { mod, pitchClass, scaleLength, type ScaleName } from './pitch.ts';
import { STEPS_PER_BAR, clampBpm } from './scheduler.ts';

export const PHRASE_BARS = 8;
export const PHRASE_STEPS = PHRASE_BARS * STEPS_PER_BAR;
export const MOTIF_BARS = 2;
export const MOTIF_STEPS = MOTIF_BARS * STEPS_PER_BAR;
/** Melody range in scale degrees relative to the lead root. */
export const MELODY_RANGE = { min: -2, max: 8 } as const;
/** Every Nth phrase rests in the B section (pad only). */
export const BREATHER_EVERY = 4;

export interface MelodyNote {
  /** Step within the phrase, 0…PHRASE_STEPS−1 (eighth notes). */
  step: number;
  /** Length in steps (≥ 1). */
  length: number;
  degree: number;
  /** 0–1. */
  velocity: number;
}

export interface Phrase {
  index: number;
  /** Monophonic, sorted by step, non-overlapping. */
  melody: MelodyNote[];
  /** Chord root (scale degree) for each of the 8 bars. */
  chords: number[];
}

/** The fixed musical identity of a theme (seeded once). */
export interface MusicIdentity {
  seed: number;
  scale: ScaleName;
  motif: MelodyNote[];
  progression: number[];
  /** Degrees the B section is sequenced by. */
  sequenceShift: number;
}

type MusicParams = Pick<ThemeAudio, 'key' | 'scale' | 'bpm' | 'lead'>;

/** 32-bit FNV-1a hash of a string. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Seed of a theme's tune. Enharmonic keys ('F#' / 'Gb') share a tune; the
 * lead timbre is included so two themes sharing key/scale/bpm still differ.
 */
export function themeMusicSeed(a: MusicParams): number {
  return hashString(`${pitchClass(a.key)}|${a.scale}|${Math.round(clampBpm(a.bpm))}|${a.lead}`);
}

// ───────────── Harmony ─────────────

const PENTATONIC_CHORDS: readonly (readonly number[])[] = [
  [0, 2, 3], // I   (C E G)
  [1, 3, 4], // ii  (D G A — open sus sound)
  [2, 4, 5], // iii (E A C = Am/E)
  [3, 4, 6], // V   (G A D — open)
  [4, 5, 7], // vi  (A C E)
];

/** Chord tones (scale degrees) of the chord built on `root`. */
export function chordTones(root: number, scale: ScaleName): number[] {
  const n = scaleLength(scale);
  if (scale === 'majorPentatonic') {
    const octave = Math.floor(root / n);
    return PENTATONIC_CHORDS[root - octave * n]!.map((d) => d + octave * n);
  }
  return [root, root + 2, root + 4];
}

/** Degrees that sound "at rest" on the tonic chord (1, 3, 5). */
export function stableDegrees(scale: ScaleName): number[] {
  return chordTones(0, scale);
}

/** The dominant degree (5th of the scale), used for "question" phrase endings. */
export function dominantDegree(scale: ScaleName): number {
  return scale === 'majorPentatonic' ? 3 : 4;
}

/**
 * Eight-bar chord progressions (root degrees) per scale. Diminished chords are
 * avoided; each progression starts on the tonic and ends leading back to it.
 */
export const PROGRESSIONS: Readonly<Record<ScaleName, readonly (readonly number[])[]>> = {
  majorPentatonic: [
    [0, 0, 4, 3, 0, 4, 1, 3],
    [0, 4, 1, 3, 0, 4, 3, 3],
    [0, 3, 4, 1, 0, 3, 1, 3],
  ],
  major: [
    [0, 0, 3, 4, 0, 5, 3, 4],
    [0, 5, 3, 4, 0, 5, 3, 4],
    [0, 3, 4, 0, 5, 3, 1, 4],
    [0, 4, 5, 3, 0, 4, 3, 4],
  ],
  lydian: [
    [0, 1, 0, 1, 5, 1, 4, 4],
    [0, 1, 5, 4, 0, 1, 2, 4],
    [0, 4, 1, 0, 5, 1, 4, 4],
  ],
  mixolydian: [
    [0, 6, 3, 0, 0, 6, 3, 6],
    [0, 3, 6, 0, 0, 3, 4, 6],
    [0, 6, 0, 3, 5, 6, 3, 3],
  ],
  dorian: [
    [0, 3, 0, 3, 0, 3, 6, 6],
    [0, 6, 3, 0, 2, 3, 6, 6],
    [0, 3, 4, 0, 2, 6, 3, 6],
  ],
};

/**
 * Nearest degree to `d` inside MELODY_RANGE whose pitch class (degree mod
 * scale length) is one of `classes`. Ties resolve downward (deterministic).
 */
export function nearestDegreeIn(d: number, classes: readonly number[], n: number): number {
  const wanted = new Set(classes.map((c) => mod(c, n)));
  let best = d;
  let bestDist = Infinity;
  for (let c = MELODY_RANGE.min; c <= MELODY_RANGE.max; c++) {
    if (!wanted.has(mod(c, n))) continue;
    const dist = Math.abs(c - d);
    if (dist < bestDist) {
      best = c;
      bestDist = dist;
    }
  }
  return best;
}

// ───────────── Motif ─────────────

type Cell = 'quarter' | 'eighths' | 'offbeat' | 'rest' | 'half';
type Density = 'sparse' | 'medium' | 'busy';

/** Note onsets of a one-beat rhythm cell: [offset in steps, length in steps]. 'half' spans two beats. */
const CELL_ONSETS: Readonly<Record<Cell, readonly (readonly [number, number])[]>> = {
  quarter: [[0, 2]],
  eighths: [
    [0, 1],
    [1, 1],
  ],
  offbeat: [[1, 1]],
  rest: [],
  half: [[0, 4]],
};

/** Faster themes get sparser melodies so the music stays calm. */
const CELL_WEIGHTS: Readonly<Record<Density, Readonly<Record<Cell, number>>>> = {
  sparse: { quarter: 5, eighths: 1, offbeat: 0.5, rest: 1.5, half: 2 },
  medium: { quarter: 4, eighths: 2.5, offbeat: 1, rest: 1, half: 1.5 },
  busy: { quarter: 3, eighths: 4, offbeat: 1.2, rest: 0.8, half: 1 },
};

function densityFor(bpm: number): Density {
  const b = clampBpm(bpm);
  return b >= 112 ? 'sparse' : b <= 84 ? 'busy' : 'medium';
}

const MOTIF_BEATS = MOTIF_STEPS / 2;
/** The last two beats of the motif form its ending (a held note or note + breath). */
const ENDING_BEAT = MOTIF_BEATS - 2;

function pickCell(rng: Rng, density: Density, allowed: readonly Cell[]): Cell {
  const w = CELL_WEIGHTS[density];
  return rng.weighted(allowed.map((item) => ({ item, weight: w[item] })));
}

/** Rhythm of the motif as [step, length] onsets within MOTIF_STEPS. */
function motifRhythm(rng: Rng, density: Density): [number, number][] {
  const onsets: [number, number][] = [];
  let beat = 0;
  while (beat < ENDING_BEAT) {
    const allowed: Cell[] =
      beat === 0
        ? ['quarter', 'eighths', 'half']
        : beat + 2 <= ENDING_BEAT
          ? ['quarter', 'eighths', 'offbeat', 'rest', 'half']
          : ['quarter', 'eighths', 'offbeat', 'rest'];
    const cell = pickCell(rng, density, allowed);
    for (const [o, len] of CELL_ONSETS[cell]) onsets.push([beat * 2 + o, len]);
    beat += cell === 'half' ? 2 : 1;
  }
  // Ending: a held note, or a note followed by a breath.
  if (rng.chance(0.6)) onsets.push([ENDING_BEAT * 2, 4]);
  else onsets.push([ENDING_BEAT * 2, 2]);
  return onsets;
}

const MOVES = [-3, -2, -1, 0, 1, 2, 3] as const;
const MOVE_WEIGHTS = [0.4, 1, 3, 1, 3, 1.2, 0.4] as const;

/** Base velocity by metric position: bar downbeat > beat 3 > other beats > offbeats. */
function metricVelocity(step: number): number {
  if (step % STEPS_PER_BAR === 0) return 0.9;
  if (step % 4 === 0) return 0.8;
  return step % 2 === 0 ? 0.72 : 0.62;
}

function velocity(step: number, rng: Rng): number {
  return Math.min(1, Math.max(0.3, metricVelocity(step) + (rng.next() - 0.5) * 0.08));
}

function reflectIntoRange(d: number, from: number): number {
  if (d >= MELODY_RANGE.min && d <= MELODY_RANGE.max) return d;
  const mirrored = 2 * from - d;
  return Math.min(MELODY_RANGE.max, Math.max(MELODY_RANGE.min, mirrored));
}

/** Generate the 2-bar motif: an arch-shaped, mostly stepwise line on a gentle rhythm. */
export function generateMotif(rng: Rng, scale: ScaleName, bpm: number): MelodyNote[] {
  const n = scaleLength(scale);
  const density = densityFor(bpm);
  let rhythm = motifRhythm(rng, density);
  // Very sparse draws are re-rolled (deterministically) so every tune has a shape.
  for (let attempt = 0; rhythm.length < 4 && attempt < 6; attempt++) rhythm = motifRhythm(rng, density);

  const notes: MelodyNote[] = [];
  let degree = rng.pick(stableDegrees(scale));
  let repeats = 0;
  rhythm.forEach(([step, length], i) => {
    if (i > 0) {
      const rising = step < MOTIF_STEPS / 2;
      const weights = MOVES.map((m, k) => {
        let w: number = MOVE_WEIGHTS[k]!;
        if ((rising && m > 0) || (!rising && m < 0)) w *= 1.4;
        if (m === 0 && repeats >= 1) w = 0;
        return { item: m, weight: w };
      });
      const move = rng.weighted(weights);
      const next = reflectIntoRange(degree + move, degree);
      repeats = next === degree ? repeats + 1 : 0;
      degree = next;
    }
    notes.push({ step, length, degree, velocity: velocity(step, rng) });
  });
  const last = notes[notes.length - 1]!;
  last.degree = nearestDegreeIn(last.degree, stableDegrees(scale), n);
  return notes;
}

/** Build the theme's fixed identity (motif, progression, sequence shift). */
export function createMusicIdentity(audio: MusicParams): MusicIdentity {
  const seed = themeMusicSeed(audio);
  const rng = new Rng(seed);
  const motif = generateMotif(rng, audio.scale, audio.bpm);
  const progression = rng.pick(PROGRESSIONS[audio.scale]).slice();
  const sequenceShift = rng.pick([2, 1, -1, 3]);
  return { seed, scale: audio.scale, motif, progression, sequenceShift };
}

// ───────────── Phrase ─────────────

function shifted(notes: readonly MelodyNote[], steps: number): MelodyNote[] {
  return notes.map((x) => ({ ...x, step: x.step + steps }));
}

function inRange(notes: readonly MelodyNote[]): boolean {
  return notes.every((x) => x.degree >= MELODY_RANGE.min && x.degree <= MELODY_RANGE.max);
}

/** Sequence the notes by `shift` degrees (or the opposite way if that leaves the range). */
function sequenced(notes: readonly MelodyNote[], shift: number): MelodyNote[] {
  for (const s of [shift, -shift]) {
    const moved = notes.map((x) => ({ ...x, degree: x.degree + s }));
    if (inRange(moved)) return moved;
  }
  return notes.map((x) => ({
    ...x,
    degree: Math.min(MELODY_RANGE.max, Math.max(MELODY_RANGE.min, x.degree + shift)),
  }));
}

function withLastDegree(notes: MelodyNote[], classes: readonly number[], n: number): MelodyNote[] {
  const last = notes[notes.length - 1];
  if (last) last.degree = nearestDegreeIn(last.degree, classes, n);
  return notes;
}

/** Gentle per-phrase variation outside the opening motif (bars 1–2) and the final note. */
function varied(melody: readonly MelodyNote[], rng: Rng): MelodyNote[] {
  const out: MelodyNote[] = [];
  melody.forEach((note, i) => {
    const protectedNote = note.step < MOTIF_STEPS || i === melody.length - 1;
    if (protectedNote) {
      out.push({ ...note });
      return;
    }
    let degree = note.degree;
    if (rng.chance(0.18)) degree = reflectIntoRange(degree + rng.sign(), degree);
    if (note.length === 2 && rng.chance(0.12)) {
      const second = reflectIntoRange(degree + rng.sign(), degree);
      out.push({ ...note, degree, length: 1 });
      out.push({ step: note.step + 1, length: 1, degree: second, velocity: metricVelocity(note.step + 1) });
      return;
    }
    out.push({ ...note, degree });
  });
  return out;
}

/** Snap strong-beat notes (beats 1 and 3) to the tones of their bar's chord. */
function harmonized(melody: readonly MelodyNote[], chords: readonly number[], scale: ScaleName): MelodyNote[] {
  const n = scaleLength(scale);
  return melody.map((note) => {
    if (note.step % 4 !== 0) return note;
    const chord = chords[Math.floor(note.step / STEPS_PER_BAR)] ?? 0;
    return { ...note, degree: nearestDegreeIn(note.degree, chordTones(chord, scale), n) };
  });
}

/** The 8-bar phrase number `index` (0 = canonical tune) of a theme. */
export function buildPhrase(identity: MusicIdentity, index: number): Phrase {
  const { motif, scale } = identity;
  const n = scaleLength(scale);
  const a = shifted(motif, 0);
  const aQuestion = withLastDegree(shifted(motif, MOTIF_STEPS), [dominantDegree(scale)], n);
  const b = sequenced(shifted(motif, 2 * MOTIF_STEPS), identity.sequenceShift);
  const aAnswer = withLastDegree(shifted(motif, 3 * MOTIF_STEPS), [0], n);

  let melody = [...a, ...aQuestion, ...b, ...aAnswer];
  if (index > 0) melody = varied(melody, new Rng(deriveSeed(identity.seed, index)));
  if (index % BREATHER_EVERY === BREATHER_EVERY - 1) {
    melody = melody.filter((x) => x.step < 2 * MOTIF_STEPS || x.step >= 3 * MOTIF_STEPS);
  }
  melody = harmonized(melody, identity.progression, scale);
  return { index, melody, chords: identity.progression.slice() };
}
